import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Button,
  CardMedia,
  IconButton,
  Input,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  useAddEmployeeMutation,
  useLazyGetEmployeeDetailsQuery,
  useUpdateEmployeeMutation,
} from "../../service/employee";
import { showError, showToast } from "../../constants/toasts";
import CloseIcon from "@mui/icons-material/Close";
import dayjs, { Dayjs } from "dayjs";
import PhoneInput from "react-phone-input-2";
import Loader from "../../constants/Loader";
import { Members } from "../../types/General";

function AddEmployee() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [updateEmployee] = useUpdateEmployeeMutation();
  const [addEmployee] = useAddEmployeeMutation();
  const [getEmployee] = useLazyGetEmployeeDetailsQuery();
  const [employeeData, setEmployeeData] = useState<Members>();
  const [image, setImage] = useState<any>("");
  const [url, setUrl] = useState("");
  const [error, setError] = useState(false);
  const [dob, setDob] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);

  const goBack = () => {
    navigate(-1); // this goes one step back in history
  };

  const handleChange1 = (event: SelectChangeEvent) => {
    const { name, value } = event.target;

    formik.setFieldValue("gender", value);
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    const file = files?.length ? files[0] : null;

    if (!file) {
      setImage("");
      return;
    }

    const MAX_SIZE = 2 * 1024 * 1024; // 2MB

    if (file.size > MAX_SIZE) {
      showError("Image size must be less than 2MB.");
      event.target.value = "";
      return;
    }

    setImage(file);
    setUrl(URL.createObjectURL(file));
  };

  const handleChangePhone = (phone: any, country: any) => {
    formik.setFieldValue("phone", phone?.replace(country.dialCode, ""));
    formik.setFieldValue("countryCode", country?.dialCode);
  };

  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      name: employeeData?.name || "",
      email: employeeData?.email || "",
      phone: employeeData?.phoneNumber?.phone || "",
      gender: employeeData?.gender || "",
      countryCode: employeeData?.phoneNumber?.countryCode || "+971",
    },
    validationSchema: Yup.object({
      name: Yup.string().required().required("Name is required"),
      email: Yup.string()
        .required("Email is required")
        .matches(
          /^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/i,
          "Please enter a valid email address"
        ),
      phone: Yup.string()
        .required("Phone Number is required")
        .min(6, "Minimum 6 digits are required")
        .max(16, "Maximum 16 digits are required"),
    }),

    onSubmit: async (values: any) => {
      formik.setSubmitting(true);

      const formData = new FormData();

      formData.append("ProfileImage", image);
      formData.append("Name", values.name);
      formData.append("DateofBirth", dayjs(dob).toISOString());
      formData.append("Phone", values.phone);
      formData.append("Email", values.email);
      formData.append("Gender", values.gender);
      formData.append("CountryCode", values.countryCode);

      // if (selectedId) {
      //   formData.append("CategoryId", selectedId);
      // }
      if (id) {
        try {
          setLoading(true);
          // const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await updateEmployee(formData).unwrap();
          setLoading(false);
          if (response?.status === 200) {
            goBack();
            showToast(response?.message || "");
          } else {
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.error(error);
        }
      } else {
        try {
          setLoading(true);
          // const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
          const response = await addEmployee(formData).unwrap();
          setLoading(false);
          if (response?.status === 200) {
            goBack();
            showToast(response?.message || "");
          } else {
            showError(response?.message || "");
          }
        } catch (error: any) {
          setLoading(false);
          showError(error?.data?.message || "");
          console.error(error);
        }
      }
    },
  });

  const getEmployeeDetail = async () => {
    setLoading(true);
    try {
      const response = await getEmployee({
        id,
      }).unwrap();
      setLoading(false);
      if (response.status === 200) {
        setEmployeeData(response?.data);
        setDob(dayjs(response?.data?.dateOfBirh));
        setUrl(response?.data?.profileImage || "");
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  useEffect(() => {
    if (id) {
      getEmployeeDetail();
    }
  }, []);

  return (
    <>
      <MainContainer>
        <Loader isLoad={loading} />
        <div className="main_title d_flex">
          <div className="left_s">
            <h1 className="back_arrow">
              {" "}
              <IconButton onClick={() => goBack()}>
                {" "}
                <ArrowBackIcon />{" "}
              </IconButton>{" "}
              Add New Employee
            </h1>
          </div>

          <div className="right_s">
            <Button
              className="btn btn_primary"
              onClick={() => {
                setError(true);
                formik.submitForm();
              }}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="cards">
          <form>
            <div className="gap_m">
              <div className="control_group w_100">
                <label>Upload Image</label>
                {url ? (
                  <div className="upload_image">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={url} alt="photo" />
                      </figure>
                      <CloseIcon
                        onClick={() => {
                          setImage("");
                          setUrl("");
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <label className="upload_image" htmlFor="icon-button-file">
                    <Input
                      sx={{ display: "none" }}
                      id="icon-button-file"
                      type="file"
                      inputProps={{
                        accept: "image/png,image/jpeg",
                      }}
                      onChange={(e: any) => {
                        handleImageUpload(e);
                        e.target.value = null;
                      }}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        <img
                          src="/static/images/user_placeholder.png"
                          alt="icon"
                        />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="icon" />
                        </span>
                      </figure>
                    </span>
                  </label>
                )}
              </div>

              <div className="control_group w_33">
                <label htmlFor="name">Name</label>
                <TextField
                  placeholder="Enter Name"
                  hiddenLabel
                  fullWidth
                  value={formik.values.name}
                  id="name"
                  name="name"
                  className="text_field"
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.name &&
                    typeof formik.errors.name === "string"
                      ? formik.errors.name
                      : ""
                  }
                />
              </div>

              <div className="control_group w_33">
                <label htmlFor="email">Email</label>
                <TextField
                  placeholder="Enter Email"
                  hiddenLabel
                  fullWidth
                  value={formik.values.email}
                  id="email"
                  name="email"
                  className="text_field"
                  onChange={(val) => {
                    if (val.target.value === " " || val.target.value === ".") {
                    } else {
                      formik.handleChange(val);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  helperText={
                    formik.touched.email &&
                    typeof formik.errors.email === "string"
                      ? formik.errors.email
                      : ""
                  }
                />
              </div>

              <div className="control_group w_33">
                <label>Phone Number*</label>
                <PhoneInput
                  enableSearch
                  value={formik.values.countryCode + formik.values.phone}
                  country={"uae"}
                  inputClass="phoneInput"
                  onChange={(phone, country) =>
                    handleChangePhone(phone, country)
                  }
                  inputStyle={{ width: "100%", backgroundColor: "#232323" }}
                  buttonClass="phoneBtn"
                  placeholder="Phone Number"
                />
                <p className="err_msg">
                  {error && !formik.values.phone?.length
                    ? "Phone number is required"
                    : ""}
                </p>
              </div>

              <div className="w_33 control_group date_field">
                <label>Date of birth</label>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    className="form_control"
                    value={dob}
                    onChange={(newValue) => setDob(newValue)}
                    format="DD/MM/YYYY"
                    maxDate={dayjs().subtract(15, "year")}
                    slots={{
                      openPickerIcon: () => (
                        <img
                          src="/static/images/calendar_icon.svg"
                          alt="icon"
                        />
                      ),
                      // textField: (params) => (
                      //   <TextField
                      //     {...params}
                      //     fullWidth
                      //     placeholder="Date of birth"
                      //     hiddenLabel
                      //   />
                      // ),
                    }}
                    desktopModeMediaQuery="(min-width:0px)"
                  />
                </LocalizationProvider>
                {error && !dob ? (
                  <h6 className="err_msg">Date of birth is required</h6>
                ) : (
                  ""
                )}
              </div>

              <div className="control_group w_50">
                <label htmlFor="demo-simple-select">Gender</label>
                <Select
                  className="dropse"
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  inputProps={{
                    "aria-label": "Without label",
                  }}
                  displayEmpty
                  value={formik.values.gender}
                  name="gender"
                  onChange={handleChange1}
                >
                  <MenuItem value="" disabled>
                    Select gender
                  </MenuItem>

                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
                {error && formik.values.gender == "" ? (
                  <h6 className="err_msg">Gender is required</h6>
                ) : (
                  ""
                )}
              </div>
            </div>
          </form>
        </div>
      </MainContainer>
    </>
  );
}

export default AddEmployee;
